import { cryptoUtil } from "./crypto";

/**
 * CryptoKey 객체를 저장하는 저장소
 * IndexedDB는 CryptoKey를 구조화 복제로 저장하므로 non-extractable 속성이 그대로 유지됩니다.
 */
export interface CryptoKeyStore {
  get(name: string): Promise<CryptoKey | null>;
  put(name: string, key: CryptoKey): Promise<void>;
  delete(name: string): Promise<void>;
}

const DB_NAME = "secure-key-store";
const STORE_NAME = "keys";

export class IndexedDbCryptoKeyStore implements CryptoKeyStore {
  private openDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      if (typeof indexedDB === "undefined") {
        reject(new Error("IndexedDB is not available"));
        return;
      }
      const request = indexedDB.open(DB_NAME, 1);
      request.onupgradeneeded = () => {
        request.result.createObjectStore(STORE_NAME);
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  private async run<T>(
    mode: IDBTransactionMode,
    action: (store: IDBObjectStore) => IDBRequest<T>
  ): Promise<T> {
    const db = await this.openDb();
    try {
      return await new Promise<T>((resolve, reject) => {
        const request = action(db.transaction(STORE_NAME, mode).objectStore(STORE_NAME));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
    } finally {
      db.close();
    }
  }

  async get(name: string): Promise<CryptoKey | null> {
    const key = await this.run<unknown>("readonly", (store) => store.get(name));
    return key instanceof CryptoKey ? key : null;
  }

  async put(name: string, key: CryptoKey): Promise<void> {
    await this.run("readwrite", (store) => store.put(key, name));
  }

  async delete(name: string): Promise<void> {
    await this.run("readwrite", (store) => store.delete(name));
  }
}

/**
 * 로그인 없이 진행하는 흐름(보호자 앱 계정 설정)에서 SecureStorage 암호화에 쓰는 브라우저 임시 키 제공자
 *
 * - 번들에 포함된 고정 키 대신, 브라우저에서 생성한 non-extractable AES 키를 사용합니다.
 * - 새로고침해도 유지되도록 IndexedDB에 보관하고, 흐름 시작 시 교체·종료 시 삭제합니다.
 * - IndexedDB를 쓸 수 없는 환경에서는 메모리에만 보관합니다(새로고침 시 소실).
 */
export class BrowserStorageKeyProvider {
  private key: CryptoKey | null = null;
  private pending: Promise<CryptoKey> | null = null;

  constructor(
    private readonly store: CryptoKeyStore,
    private readonly generateKey: () => Promise<CryptoKey>,
    private readonly keyName: string = "browser-storage-key"
  ) {}

  /**
   * 보관 중인 키를 반환하고, 없으면 새로 생성해 저장합니다.
   */
  async getOrCreateKey(): Promise<CryptoKey> {
    if (this.key) {
      return this.key;
    }

    if (!this.pending) {
      this.pending = (async () => {
        try {
          const storedKey = await this.loadStoredKey();
          this.key = storedKey ?? (await this.createKey());
          return this.key;
        } finally {
          this.pending = null;
        }
      })();
    }

    return this.pending;
  }

  /**
   * 기존 키를 버리고 새 키로 교체합니다. 이전 키로 암호화된 값은 더 이상 복호화되지 않습니다.
   */
  async rotateKey(): Promise<void> {
    await this.clear();
    this.key = await this.createKey();
  }

  /**
   * 메모리와 IndexedDB에서 키를 삭제합니다.
   */
  async clear(): Promise<void> {
    this.key = null;
    try {
      await this.store.delete(this.keyName);
    } catch (error: unknown) {
      console.error("[BrowserStorageKeyProvider] :: clear :: 키 삭제 실패", error);
    }
  }

  private async loadStoredKey(): Promise<CryptoKey | null> {
    try {
      return await this.store.get(this.keyName);
    } catch (error: unknown) {
      console.error("[BrowserStorageKeyProvider] :: loadStoredKey :: 키 조회 실패", error);
      return null;
    }
  }

  private async createKey(): Promise<CryptoKey> {
    const key = await this.generateKey();
    try {
      await this.store.put(this.keyName, key);
    } catch (error: unknown) {
      console.error("[BrowserStorageKeyProvider] :: createKey :: 키 저장 실패, 메모리에만 보관", error);
    }
    return key;
  }
}

export const browserStorageKeyProvider = new BrowserStorageKeyProvider(
  new IndexedDbCryptoKeyStore(),
  () => cryptoUtil.generateAesKey()
);
